"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.constants = void 0;
const houseType_1 = require("./houseType");
const tieredPrices_1 = __importDefault(require("./tieredPrices"));
/** A bunch of constants used for energy savings calculations, and limit checking */
const calculationConstants = {
    // Include tax return in calculations only when total tax is under €2.450 (Harm/Leo @ 31-01-2020)
    // From https://www.belastingdienst.nl/wps/wcm/connect/nl/btw/content/btw-terugvragen-voor-zonnepanelen-ik-ben-particulier
    // "Kiest u voor de KOR en vraagt u meer dan € 2.500 btw terug? Dan krijgt u te maken met de btw-herziening."
    pvTaxReturnLimit: 2499.99,
    /**
     * For PV electricity production:
     * The amount of kWh consumption over 10.000 that gets discounted. Use energyPrices.discountedElectricity
     * @example if you consume 15.000kWh and are going to produce 8.000kWh, you save 5.000 discounted kWh and 3.000 non-discounted kWh
     */
    kwhDiscountMinimum: 10000,
    // From MilieuCentraal, April 2020: https://www.milieucentraal.nl/energie-besparen/energiesubsidies-en-leningen/rendement-energiebesparing/
    electricityInflation: 1.02,
    gasInflation: 1.03,
    savingsInterest: 1.014,
    savingsTerm: 30,
    megaJouleTokWh: 1 / 3.6,
    megaJouleToGasM3: 1 / 35.17,
};
exports.constants = {
    // Average yearly kWh for amount of residents (Nibud, 2017)
    averageElectricityConsumption: {
        one: 1840,
        two: 2930,
        three: 3590,
        four: 4040,
        five: 4520,
        six: 4830,
    },
    comfort: {
        pvSystem: 2,
        wallInsulation: 4,
        floorInsulation: 5,
        crawlspaceInsulation: 3,
        innerRoofInsulation: 4,
        atticFloorInsulation: 4,
        centralHeatingBoiler: 5,
        windowGlazing: 5,
        windowFrames: 4,
        hybridHeatPump: 4,
        electricHeatPump: 5,
        airToAirHeatPump: 5,
        miscellaneous: 1,
        sedum: 1,
        chargingBox: 2,
        underfloorHeating: 2,
        homeBattery: 0,
    },
    emissionFactors: {
        // https://co2emissiefactoren.nl/lijst-emissiefactoren/#totale_lijst
        // Emission per kWh and m3
        electricity: 0.572,
        gas: 1.78,
        // Emission per liter
        diesel: 3.23,
        gasoline: 2.74,
        kerosine: 2.25,
        lpg: 1.086,
        // Emission per km per passenger in Economy class
        // Business class adds double
        // First class adds triple
        airplaneLg: 0.147,
        airplaneMd: 0.2,
        airplaneSm: 0.297,
        // Emission per km per vehicle
        car: 0.22,
        // Emission per km per passenger
        train: 0.006,
        // Compensation per tree per year
        tree: 21.77,
        // Emission per cow per year
        cow: 2875,
        hamburger: 3.36, // 200g of beef
    },
    energyLabels: {
        // https://zoek.officielebekendmakingen.nl/stcrt-2017-70843.html
        // Corresponding maximum energy index for each label class (2018)
        'A++++': 0,
        'A+++': 0,
        'A++': 0.6,
        'A+': 0.8,
        A: 1.2,
        B: 1.4,
        C: 1.8,
        D: 2.1,
        E: 2.4,
        F: 2.7,
        G: 9999,
    },
    // These are the energy prices we base all financial energy savings calculations on by default
    energyPrices: {
        startDate: new Date('2024-05-01'),
        gas: 1.34,
        electricity: 0.31,
        discountedElectricity: 0.31 - 0.1088 + 0.04089,
        gasNetworkOperator: 191.32, // fixed cost for having a connection to the gas network
    },
    // https://www.cbs.nl/nl-nl/cijfers/detail/85592NED
    // prettier-ignore
    historicalEnergyPrices: [
        { startDate: new Date('2019-08-01'), gas: 0.75239, electricity: 0.21901, discountedElectricity: 0.21901 - 0.09863 - 0.01421, gasNetworkOperator: 177.61 },
        { startDate: new Date('2019-09-01'), gas: 0.75049, electricity: 0.21941, discountedElectricity: 0.21941 - 0.09863 - 0.01421, gasNetworkOperator: 177.61 },
        { startDate: new Date('2019-10-01'), gas: 0.74779, electricity: 0.21911, discountedElectricity: 0.21911 - 0.09863 - 0.01421, gasNetworkOperator: 177.61 },
        { startDate: new Date('2019-11-01'), gas: 0.75009, electricity: 0.21951, discountedElectricity: 0.21951 - 0.09863 - 0.01421, gasNetworkOperator: 177.61 },
        { startDate: new Date('2019-12-01'), gas: 0.74819, electricity: 0.21871, discountedElectricity: 0.21871 - 0.09863 - 0.01421, gasNetworkOperator: 177.61 },
        { startDate: new Date('2020-01-01'), gas: 0.81949, electricity: 0.22805, discountedElectricity: 0.22805 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-02-01'), gas: 0.81369, electricity: 0.22675, discountedElectricity: 0.22675 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-03-01'), gas: 0.80279, electricity: 0.22455, discountedElectricity: 0.22455 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-04-01'), gas: 0.79099, electricity: 0.22195, discountedElectricity: 0.22195 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-05-01'), gas: 0.78559, electricity: 0.22025, discountedElectricity: 0.22025 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-06-01'), gas: 0.78109, electricity: 0.21945, discountedElectricity: 0.21945 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-07-01'), gas: 0.75839, electricity: 0.21875, discountedElectricity: 0.21875 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-08-01'), gas: 0.75979, electricity: 0.21975, discountedElectricity: 0.21975 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-09-01'), gas: 0.76019, electricity: 0.22035, discountedElectricity: 0.22035 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-10-01'), gas: 0.76169, electricity: 0.22045, discountedElectricity: 0.22045 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-11-01'), gas: 0.76639, electricity: 0.22135, discountedElectricity: 0.22135 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2020-12-01'), gas: 0.76879, electricity: 0.22125, discountedElectricity: 0.22125 - 0.09770 - 0.01353, gasNetworkOperator: 185.36 },
        { startDate: new Date('2021-01-01'), gas: 0.79073, electricity: 0.22008, discountedElectricity: 0.22008 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-02-01'), gas: 0.80053, electricity: 0.22388, discountedElectricity: 0.22388 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-03-01'), gas: 0.80893, electricity: 0.22568, discountedElectricity: 0.22568 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-04-01'), gas: 0.81373, electricity: 0.22718, discountedElectricity: 0.22718 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-05-01'), gas: 0.81333, electricity: 0.22828, discountedElectricity: 0.22828 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-06-01'), gas: 0.82623, electricity: 0.23158, discountedElectricity: 0.23158 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-07-01'), gas: 0.86763, electricity: 0.24318, discountedElectricity: 0.24318 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-08-01'), gas: 0.88113, electricity: 0.24458, discountedElectricity: 0.24458 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-09-01'), gas: 0.93013, electricity: 0.25678, discountedElectricity: 0.25678 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-10-01'), gas: 1.05253, electricity: 0.27738, discountedElectricity: 0.27738 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-11-01'), gas: 1.27263, electricity: 0.32588, discountedElectricity: 0.32588 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2021-12-01'), gas: 1.41873, electricity: 0.36918, discountedElectricity: 0.36918 - 0.09428 - 0.05164, gasNetworkOperator: 187.85 },
        { startDate: new Date('2022-01-01'), gas: 1.73977, electricity: 0.39833, discountedElectricity: 0.39833 - 0.03679 - 0.04361, gasNetworkOperator: 191.32 },
        { startDate: new Date('2022-02-01'), gas: 1.66887, electricity: 0.37803, discountedElectricity: 0.37803 - 0.03679 - 0.04361, gasNetworkOperator: 191.32 },
        { startDate: new Date('2022-03-01'), gas: 2.38927, electricity: 0.56283, discountedElectricity: 0.56283 - 0.03679 - 0.04361, gasNetworkOperator: 191.32 },
        { startDate: new Date('2022-04-01'), gas: 2.22857, electricity: 0.51773, discountedElectricity: 0.51773 - 0.03679 - 0.04361, gasNetworkOperator: 191.32 },
        { startDate: new Date('2022-05-01'), gas: 1.96617, electricity: 0.44273, discountedElectricity: 0.44273 - 0.03679 - 0.04361, gasNetworkOperator: 191.32 },
        { startDate: new Date('2022-06-01'), gas: 1.76227, electricity: 0.41563, discountedElectricity: 0.41563 - 0.03679 - 0.04361, gasNetworkOperator: 191.32 },
        { startDate: new Date('2022-07-01'), gas: 2.0810, electricity: 0.49205, discountedElectricity: 0.49205 - 0.03679 - 0.04361, gasNetworkOperator: 172.35 },
        { startDate: new Date('2022-08-01'), gas: 2.6049, electricity: 0.57615, discountedElectricity: 0.57615 - 0.03679 - 0.04361, gasNetworkOperator: 172.35 },
        { startDate: new Date('2022-09-01'), gas: 3.2608, electricity: 0.71725, discountedElectricity: 0.71725 - 0.03679 - 0.04361, gasNetworkOperator: 172.35 },
        { startDate: new Date('2022-10-01'), gas: 3.2732, electricity: 0.73215, discountedElectricity: 0.73215 - 0.03679 - 0.04361, gasNetworkOperator: 172.35 },
        { startDate: new Date('2022-11-01'), gas: 2.3103, electricity: 0.59995, discountedElectricity: 0.59995 - 0.03679 - 0.04361, gasNetworkOperator: 172.35 },
        { startDate: new Date('2022-12-01'), gas: 2.3103, electricity: 0.59995, discountedElectricity: 0.59995 - 0.03679 - 0.04361, gasNetworkOperator: 172.35 },
        // Price ceiling in effect from 2023-01-01
        // Using the average of contract price and ceiling price for base prices
        // (i.e. January is (1.9961 + 0.9235) / 2 = 1.4598, then adding €0.59266 tax = 2.05246. Same for electricity.)
        { startDate: new Date('2023-01-01'), gas: 2.05246, electricity: 0.5409, discountedElectricity: 0.5409 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-02-01'), gas: 1.96801, electricity: 0.5175, discountedElectricity: 0.5175 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-03-01'), gas: 1.89686, electricity: 0.49225, discountedElectricity: 0.49225 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-04-01'), gas: 1.58861, electricity: 0.4144, discountedElectricity: 0.4144 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-05-01'), gas: 1.44891, electricity: 0.38615, discountedElectricity: 0.38615 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-06-01'), gas: 1.41041, electricity: 0.3761, discountedElectricity: 0.3761 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-07-01'), gas: 1.28076, electricity: 0.3523, discountedElectricity: 0.3523 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-08-01'), gas: 1.25836, electricity: 0.34765, discountedElectricity: 0.34765 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-09-01'), gas: 1.25736, electricity: 0.3479, discountedElectricity: 0.3479 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-10-01'), gas: 1.25016, electricity: 0.3438, discountedElectricity: 0.3438 - 0.15245, gasNetworkOperator: 217.69 },
        { startDate: new Date('2023-11-01'), gas: 1.27321, electricity: 0.34895, discountedElectricity: 0.34895 - 0.15245, gasNetworkOperator: 217.69 },
    ],
    calculations: calculationConstants,
    houseTypeIds: houseType_1.HOUSE_TYPE_IDS,
    middleApartmentHouseTypes: houseType_1.MIDDLE_APARTMENT_HOUSE_TYPES,
    roofApartmentHouseTypes: houseType_1.APARTMENT_HOUSE_TYPES_WITH_ROOF,
    floorApartmentHouseTypes: houseType_1.APARTMENT_HOUSE_TYPES_WITH_FLOOR,
    slurperConsumption: {
        // average kWh per year
        heatPump: 4000,
        electricCar: 2300,
        swimmingPool: 2400,
        airConditioning: 300,
        underfloorHeating: 2500,
        waterBed: 750,
    },
    /** Default markup on purchase price for calculating the default retail price (source: Mark, 03-03-2020)
     * @deprecated These used to be used for calculating retail prices, which are now specified manually per product/installer pair (ProductPrice).
     */
    defaultMarkup: {
        // NOTE: These need to be kept in sync with those in the HS. These need to be phased out
        wallInsulation: { default: 0.6, min: 0.35, max: 1 },
        floorInsulation: { default: 0.6, min: 0.35, max: 1 },
        crawlspaceInsulation: { default: 0.45, min: 0.35, max: 1 },
        innerRoofInsulation: { default: 0.25, min: 0.15, max: 0.4 },
        atticFloorInsulation: { default: 0.25, min: 0.15, max: 0.4 },
        // roofRenovation: { default: 0.1, min: 0.06, max: 0.15 },
        pvSystem: { default: 0.43, min: 0.15, max: 0.6 },
        windowGlazing: { default: 0.43, min: 0.25, max: 0.6 },
        windowFrames: { default: 0.3, min: 0.25, max: 0.5 },
        centralHeatingBoiler: { default: 0.175, min: 0.15, max: 0.4 },
        electricHeatPump: { default: 0.1, min: 0.05, max: 0.2 },
        hybridHeatPump: { default: 0.34, min: 0.05, max: 0.6 },
        airToAirHeatPump: { default: 0.34, min: 0.05, max: 0.6 },
        miscellaneous: { default: 0.34, min: 0, max: 1 },
        sedum: { default: 0.34, min: 0, max: 1 },
        chargingBox: { default: 0.34, min: 0, max: 1 },
        underfloorHeating: { default: 0.34, min: 0, max: 1 },
        homeBattery: { default: 0.34, min: 0, max: 1 },
    },
    tieredPrices: tieredPrices_1.default,
};
// No: maybe some day, but for now, the history (average) energy prices and energy prices used for calculations are completely separate
// constants.energyPrices = constants.historicalEnergyPrices[constants.historicalEnergyPrices.length - 1];
exports.default = exports.constants;
